import angular from "angular";
import { saveAs } from "file-saver";
import {
  Calculation, CalculationListItem, CalendarEvent, DatasetListItem, DateRange, Employment, EmploymentListItem, EmploymentLogic, EmploymentRelationTaxcard, ExternalDialogData,
  TaxCard2019Logic,
  Taxcard, Translations
} from "@salaxy/core";

import { EditDialogKnownActions, EditDialogResult, ExternalDialogConfig } from "./model";
import type { EmploymentDetailsTabs } from "./model";

import { CrudControllerBase } from "../../controllers/bases/CrudControllerBase";
import { SessionService } from "../SessionService";

/**
 * Provides misc. user interface helper methods that are related to AngularJS.
 * Please note that these methods do NOT connect to API (load/save) to separate UI logic from business logic:
 * The business logic is in separate UiCrudHelpers class.
 */
export class UiHelpers {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["$uibModal", "$location", "$rootScope", "$window", "$timeout", "SessionService", "$uibModalStack"];

  /**
   * Creates a new UiHelpers with dependency injection.
   */
  constructor(
    private $uibModal: angular.ui.bootstrap.IModalService,
    private $location: angular.ILocationService,
    private $rootScope: angular.IRootScopeService,
    private $window: angular.IWindowService,

    private $timeout: angular.ITimeoutService,
    private sessionService: SessionService,
    private $uibModalStack: angular.ui.bootstrap.IModalStackService,
  ) {
    this.init();
  }

  /**
   * Shows an edit dialog - typically for a list item, but potentially for some other editable item.
   * Alternatively, you may use showDialog() if you wish to specify your own controller (with more logic)
   * @param templateUrl - Template url.
   * @param item - The item that is being edited. The logic will support change tracking and reset on this item.
   * @param logic - Additional logic: Helper functions, metadata etc. that view can use to contruct the UI.
   * @param size - Optional size parameter: sm, lg, xl or ''. Default is empty string (normal size).
   * @param controller Optional controller for the modal instance.
   * The item and logic are passed to controller with dependency injection name "EditDialogParameters".
   * @param options Rarely used dialog options.
   * @param options.template If set, ignores the templateUrl and uses this inline HTML template instead.
   * @returns A promise that resolves to the result from the dialog.
   * @example
   * this.uiHelpers.showDialog("salaxy-components/modals/account/AuthorizedAccountAdd.html", "AccountAuthorizationController")
   */
  public openEditDialog<TItem>(templateUrl: string, item: TItem, logic: any, size: "sm" | "lg" | "xl" | "" = "",
    controller = "EditDialogController", options: {
      template?: string,
    } = {},
  )
    : Promise<EditDialogResult<TItem>> {
    const current = angular.copy(item);
    const modalOptions: angular.ui.bootstrap.IModalSettings = {
      backdrop: "static",
      ariaLabelledBy: "modal-title",
      ariaDescribedBy: "modal-body",
      template: options.template,
      templateUrl: options.template ? null : templateUrl,
      windowTemplateUrl: "salaxy-components/modals/ui/DialogWindow.html",
      controller,
      controllerAs: "$ctrl",
      size,
      resolve: {
        EditDialogParameters: () => {
          return {
            current,
            logic,
          };
        },
        $element: null,
        $attrs: null,
      },
    };
    const modal = this.$uibModal.open(modalOptions);
    return Promise.resolve(modal.result
      .then((modalResult) => {
        const action = (angular.isString(modalResult) ? modalResult : (modalResult || {}).action) || EditDialogKnownActions.Cancel;
        const hasChanges = !angular.equals(current, item);
        if (action !== EditDialogKnownActions.Cancel && hasChanges) {
          angular.copy(current, item);
        }
        const result: EditDialogResult<TItem> = {
          action,
          result: modalResult,
          item,
          logic,
          hasChanges,
        };
        return result;
      })
      .catch(() => {
        const result: EditDialogResult<TItem> = {
          action: EditDialogKnownActions.Cancel,
          result: null,
          item,
          logic,
          hasChanges: false,
        };
        return result;
      }));
  }

  private externalWindows: {
    [name: string]: {
      win: any,
      url: string,
      listener: (this: Window, ev: {
        /** The object passed from the other window. */
        data: any,
        /** The origin of the window that sent the message at the time postMessage was called. */
        origin: string,
        /** A reference to the window object that sent the message */
        source: MessageEventSource | null,
      }) => any,
    }
  } = {};

  /**
   * Shows an external dialog that may be in an untrusted domain.
   * Enables cross-site communication using postMessage events.
   * @param name Name of the unique window
   * @param url Url to show in the Window
   * @param item The item that is being edited. The logic will support change tracking and reset on this item.
   * @param config Configuration of the opened window.
   * @param resolver Optional function for checking if the promise is resolved.
   */
  public showExternalDialog<TData>(name: string, url: string, item: TData, config?: ExternalDialogConfig, resolver?: (ev: {
    /** The object passed from the other window. */
    data: ExternalDialogData<TData>,
    /** The origin of the window that sent the message at the time postMessage was called. */
    origin: string,
    /** A reference to the window object that sent the message */
    source: MessageEventSource | null,
  }) => boolean,
  /** Optional callback function to hide any loading indicator shown while opening the external dialog. */
  hideExternalDialogLoader?: () => void
  )
    : Promise<ExternalDialogData<TData> & { event?: any }> {
    const browserWindow = window;
    config = config || {};

    if (this.externalWindows[name]?.listener) {
      // Cancel the previous promise and unregister event.
      this.externalWindows[name].listener.call(browserWindow, {
        data: {
          action: "cancel",
        },
        origin: null,
        source: null,
      });
      browserWindow.removeEventListener("message", this.externalWindows[name].listener);
    }

    const width = config.width || 600;
    const height = config.height || 700;
    // access to browserWindow.top is blocked if the site is run in an iframe in a cross domain environment
    // have to use current window only
    const y = browserWindow.outerHeight / 2 + browserWindow.screenY - (height / 2);
    const x = browserWindow.outerWidth / 2 + browserWindow.screenX - (width / 2);
    const windowFeatures = `toolbar=no, menubar=no, width=${width}, height=${height}, top=${y}, left=${x}`;

    const hostUrl = new URL(url);
    const popupBaseUrl = `${hostUrl.protocol}//${hostUrl.host}`;
    url += (url.indexOf("?") > 0 ? "&" : "?") + "data=" + encodeURIComponent(btoa(JSON.stringify(item)));
    if (url.indexOf("http") !== 0) {
      url += "&access_token=" + this.sessionService.getCurrentToken();
    }

    if (!this.externalWindows[name]?.win || this.externalWindows[name].win.closed || this.externalWindows[name].url !== url) {
      // According to best practice in https://developer.mozilla.org/en-US/docs/Web/API/Window/open
      this.externalWindows[name] = {
        url,
        win: window.open(url, name, windowFeatures),
        listener: null,
      };
    }
    // Focus and check if popup blocked
    try {
      this.externalWindows[name].win.focus();
    }
    catch (e) {
      hideExternalDialogLoader?.();
      return this.showAlert("Ponnahdusikkuna estetty selaimen asetuksissa", "Toiminto edellyttää, että sallit ponnahdusikkunan avattavan osoitteeseen " + popupBaseUrl + ".", "Sulje").then(() => {
        return Promise.resolve({
          action: "cancel",
          hasChanges: false,
          item,
        });
      });
    }
    hideExternalDialogLoader?.();
    return new Promise<ExternalDialogData<TData> & { event: any }>((resolve) => {
      this.externalWindows[name].listener = (ev: {
        /** The object passed from the other window. */
        data: ExternalDialogData<TData>,
        /** The origin of the window that sent the message at the time postMessage was called. */
        origin: string,
        /** A reference to the window object that sent the message */
        source: MessageEventSource | null,
      }) => {
        console.debug("Message received", ev);
        if (!resolver ||
          resolver(ev)) {
          // HACK: Check origin/source.
          const action = ev.data.action || "cancel";
          const hasChanges = !angular.equals(ev.data.item, item);
          if (action !== EditDialogKnownActions.Cancel && hasChanges) {
            angular.copy(ev.data.item, item);
          }

          browserWindow.removeEventListener("message", this.externalWindows[name].listener);

          resolve({
            action,
            hasChanges,
            item,
            event: ev
          });
        }
      };
      // add the listener for receiving a message from the popup
      browserWindow.addEventListener("message", this.externalWindows[name].listener, { once: !resolver });
    });
  }

  /**
   * Shows a custom dialog using the given controller and templateUrl.
   * Optionally injects the given data into the controller as 'data'.
   * NOTE: There is an alternative - more simple - modal dialog pattern openEditDialog() if you do not need a custom controller or windowTemplateUrl.
   * @param templateUrl - Template url.
   * @param controller  - Controller for the dialog.
   * @param data - Optional data to inject into the controller.
   * @param windowTemplateUrl - Optional template url for the window. Defaults to 'salaxy-components/modals/ui/DialogWindow.html'. If "none", no window is used.
   * @param size - Optional size parameter: sm, lg or ''.
   * @param backdrop - Optional. Controls the presence of a backdrop. Allowed value true, false (no backdrop) 'static' (default) backdrop is present but modal window is not closed when clicking outside of the modal window.
   * @returns A promise that resolves to the result from the dialog.
   * @example
   * this.uiHelpers.showDialog("salaxy-components/modals/account/AuthorizedAccountAdd.html", "AccountAuthorizationController")
   */
  public showDialog(
    templateUrl: string,
    controller?: string | any,
    data?: any,
    windowTemplateUrl?: string,
    size?: "sm" | "lg" | "landscape" | "xl" | "",
    backdrop: string | boolean = "static",
  ): Promise<any> {

    const $attrs: any = {};
    if (controller instanceof CrudControllerBase) {
      $attrs.onCreateNew = controller.onCreateNew;
      $attrs.onListSelect = controller.onListSelect;
      $attrs.onDelete = controller.onDelete;
    }

    const options: angular.ui.bootstrap.IModalSettings = {
      backdrop,
      ariaLabelledBy: "modal-title",
      ariaDescribedBy: "modal-body",
      templateUrl,
      windowTemplateUrl: windowTemplateUrl || "salaxy-components/modals/ui/DialogWindow.html",
      controller: controller || "ModalGenericDialogController",
      controllerAs: "$ctrl",
      size,
      resolve: {
        data: () => {
          return data;
        },
        $attrs,
      },
    };
    if (options.windowTemplateUrl === "none") {
      delete options.windowTemplateUrl;
    }
    const modal = this.$uibModal.open(options);

    return Promise.resolve(modal.result)
      .then((value) => {
        return value;
      })
      .catch((reason) => {
        if (reason === "backdrop click" || reason === "dismissAll") {
          return null;
        }
        throw new Error("" + reason);
      });
  }

  /**
   * Close all existing modals from modal stack
   */
  public dismissAllModals() {
    if (this.$uibModalStack) {
      this.$uibModalStack.dismissAll("dismissAll");
    }
  }

  /**
   * Shows a customized loading component (spinner) with an optional message.
   * Returns an object with a dismiss function to call for closing the dialog.
   * Note that there is a separate generic directive 'salaxy-loader' for indicating
   * http-traffic.
   * @param heading Translation key for heading text for the loader.
   * Default text is "SALAXY.UI_Terms.loading", but you may set this to null for just the spinner.
   * @param text Optional translation key for additional text paragraph describing what is happening.
   * @returns An object with dismiss function to close the dialog.
   * @example
   * ```ts
   * const loader = this.uiHelper.showLoading("SALAXY.UI_Terms.isSaving", "SALAXY.UI_Terms.pleaseWait");
   * setTimeout(() => {
   *   loader.dismiss();
   * }, 2000);
   * ```
   */
  public showLoading(heading = "SALAXY.UI_Terms.loading", text?: string, staticBackdrop = false): {
    /** Closes the dialog */
    dismiss: () => void,
    /** Changes the heading text after opening */
    setHeading: (heading: string) => void,
    /** Changes the text after opening */
    setText: (text: string) => void,
  } {
    const data = {
      heading,
      text,
    };
    const options: angular.ui.bootstrap.IModalSettings = {
      ariaLabelledBy: "modal-title",
      ariaDescribedBy: "modal-body",
      templateUrl: "salaxy-components/modals/ui/Loading.html",
      controller: "ModalGenericDialogController",
      controllerAs: "$ctrl",
      windowClass: "salaxy-component",
      size: "sm",
      resolve: { data },
      backdrop: staticBackdrop ? "static" : true
    };
    const modal = this.$uibModal.open(options);

    // prevent possibly unhandled rejection errors
    modal.result
      .then(() => {
        // do nothing
      })
      .catch(() => {
        // do nothing
      });

    return {
      dismiss: () => {
        this.$timeout(() => modal.dismiss());
      },
      setHeading: (heading: string) => {
        this.$timeout(() => { data.heading = heading; });
      },
      setText: (text: string) => {
        this.$timeout(() => { data.text = text; });
      },
    };
  }

  /**
   * Shows a confirm dialog. Like window.confirm() in JavaScript.
   * Returns true if a user clicks OK -button. Otherwise (cancel or close) returns false.
   * @param heading Heading text of the confirm question as translation key.
   * @param text Optional additional text paragraph describing what is happening.
   * @param okText Possibility to override the OK button text. The given text is run through translation.
   * @param cancelText Possibility to override the Cancel button text. The given text is run through translation.
   * @param html Optional HTML content to show in the dialog. If provided, text parameter is ignored.
   * @returns A promise that resolves to true if user clicks OK, otherwise false.
   * @example
   * ```ts
   * this.uiHelpers
   *     .showConfirm("SALAXY.UI_Terms.areYouSure", "SALAXY.UI_Terms.sureToDeleteRecord")
   *     .then((result) => { this.uiHelpers.showAlert("result", result); })
   * ```
   */
  public showConfirm(heading: string, text?: string, okText = "SALAXY.UI_Terms.ok", cancelText = "SALAXY.UI_Terms.cancel", html: string = null): Promise<boolean> {
    const options: angular.ui.bootstrap.IModalSettings = {
      ariaLabelledBy: "modal-title",
      ariaDescribedBy: "modal-body",
      templateUrl: "salaxy-components/modals/ui/Confirm.html",
      windowTemplateUrl: "salaxy-components/modals/ui/DialogWindow.html",
      controller: "ModalGenericDialogController",
      controllerAs: "$ctrl",
      resolve: {
        data: {
          heading,
          text,
          okText,
          cancelText,
          html
        },
      },
    };
    const modal = this.$uibModal.open(options);
    return Promise.resolve(modal.result)
      .then((result) => {
        return result;
      })
      .catch(() => {
        return false;
      });
  }

  /**
   * Shows a date range dialog that modifies the date range of the given object.
   * @param target Target object, currently both DateRange and Calculation are supported.
   * Please note that the resulting EditDialogResult is always a DateRange though.
   * @param title Title text for the dialog
   * @param okText Possibility to override the button text. The given text is run through translation.
   * @param cancelText Possibility to override the button text. The given text is run through translation.
   * @returns EditDialogResult with the edited DateRange. Note that this is DateRange even if the object to edit is Calculation.
   */
  public showDateRange(target: DateRange | Calculation, title: string, okText = "SALAXY.UI_Terms.save", cancelText = "SALAXY.UI_Terms.cancel"): Promise<EditDialogResult<DateRange>> {
    let dateRange: DateRange;
    const calc = (target as Calculation).info ? (target as Calculation) : null;
    if (calc) {
      dateRange = {
        start: calc.info.workStartDate,
        end: calc.info.workEndDate,
        daysCount: calc.framework.numberOfDays,
      };
    } else {
      dateRange = target as DateRange;
    }
    return this.openEditDialog("salaxy-components/modals/date-range/DateRange.html", dateRange, {
      title,
      okText,
      cancelText,
    }, "sm").then((dialogResult) => {
      if (dialogResult.action === "ok" && calc) {
        // If the editable item was DateRange, those changes are already committed.
        calc.info.workStartDate = dialogResult.item.start;
        calc.info.workEndDate = dialogResult.item.end;
        calc.framework.numberOfDays = dialogResult.item.daysCount;
      }
      return dialogResult;
    });
  }

  /**
   * Shows an alert dialog. Like window.alert() in JavaScript.
   * @param heading Heading text as translation key
   * @param text Optional additional text paragraph describing what is happening.
   * @param okText Possibility to override the button text. The given text is run through translation.
   * @returns A promise with boolean: True if OK button was clicked, false if the dialog is closed / dismissed in another way.
   * @example this.uiHelpers.showAlert("Please note", "There is this thing that we want to tell you.");
   */
  public showAlert(heading: string, text?: string, okText = "SALAXY.UI_Terms.ok"): Promise<boolean> {
    const options: angular.ui.bootstrap.IModalSettings = {
      ariaLabelledBy: "modal-title",
      ariaDescribedBy: "modal-body",
      templateUrl: "salaxy-components/modals/ui/Alert.html",
      controller: "ModalGenericDialogController",
      controllerAs: "$ctrl",
      windowTemplateUrl: "salaxy-components/modals/ui/DialogWindow.html",
      resolve: {
        data: {
          heading,
          text,
          okText,
        },
      },
    };
    const modal = this.$uibModal.open(options);
    return Promise.resolve(modal.result)
      .then((result) => {
        return result;
      })
      .catch(() => {
        return false;
      });
  }

  /**
   * Shows a dialog for editing an existing employment.
   * NOTE: This method does not Save the Worker: The caller must do it separately.
   * @param employment - Worker Employment edited in dialog.
   * @param initialTab - Tab to open.
   * @param buttonTypes "default" shows standard save button,
   * "updateCalc" shows two buttons "ok" for full calc update and "ok-no-rows" update without touching default rows.
   * @returns Dialog result. Note that OK result may be either "ok" or "ok-no-rows".
   * The latter means that for calculation only worker info should be updated, not the default rows.
   */
  public openEditEmploymentDialog(
    employment: Employment,
    initialTab: EmploymentDetailsTabs = null,
    buttonTypes: "default" | "updateCalc" = "default",
  ): Promise<EditDialogResult<Employment>> {
    return this.openEditDialog(
      "salaxy-components/worker/EmploymentEditModal.html", employment, {
      initialTab,
      buttonTypes,
      title: Translations.get("SALAXY.NG1.WorkerDetailsComponent.workerEdit.title"),
    }, "xl");
  }

  /**
   * Opens wizard for creating a new employment relation in a modal dialog.
   * NOTE: This method does not Save the Employment relation: The caller must do it separately.
   * @returns Dialog result with the new employment relation data and potential Taxcard (if manual) that should be created.
   * Only create if action is "ok".
   */
  public openNewEmploymentWizard(): Promise<EditDialogResult<{
    /** Employment relation that should be created */
    employment: Employment,
    /** Taxcard that should be created (if it should be created). */
    taxcard?: Taxcard,
  }>> {
    const employment = EmploymentLogic.getBlank();
    return this.openEditDialog("salaxy-components/worker/EmploymentWizard/index.html", employment, null, "lg", "EmploymentWizardController")
      .then((dialogResult) => {
        const employment: Employment = dialogResult.result.employment;
        let taxcard: Taxcard | undefined = undefined;
        if (dialogResult.action == EditDialogKnownActions.Ok && employment.info.taxcard == EmploymentRelationTaxcard.Manual) {
          taxcard = dialogResult.result.taxcard;
        }
        return {
          action: dialogResult.action,
          item: { employment, taxcard },
          hasChanges: true,
          logic: dialogResult.logic,
          result: dialogResult.action = EditDialogKnownActions.Ok ? EditDialogKnownActions.Ok : EditDialogKnownActions.Cancel,
        };
      });
  }

  /**
   * Opens a new dialog for adding a new taxcard.
   * @param personalId Worker personal id is required.
   */
  public openNewTaxcardDialog(personalId: string): Promise<EditDialogResult<Taxcard>> {
    if (!personalId) {
      throw new Error("Personal ID is required.");
    }
    const item = TaxCard2019Logic.getBlank(personalId);
    return this.openEditDialog("salaxy-components/modals/worker/taxcard-new.html", item, {});
  }

  /**
   * Opens a dialog for editing a calendar event.
   * @param calEvent The calendar event that should be created / edited.
   * @param title Title for the dialog.
   * @param template Template to use in create / editing.
   * Simplified is designed for adding scheduling to Calculation / Payroll.
   * @returns Standard dialog result for calendar event.
   */
  public openEditCalendarEvent(calEvent: CalendarEvent, title: string, template: "default" | "simplified" = "default"): Promise<EditDialogResult<CalendarEvent>> {
    const okText = "SALAXY.UI_Terms.save";
    const cancelText = "SALAXY.UI_Terms.cancel";
    const isSimplified = template === "simplified";
    return this.openEditDialog("salaxy-components/modals/workflow/CalendarEventEdit.html", calEvent, { title, okText, cancelText, isSimplified }, "lg");
  }

  /**
   * Opens the list of calculations into a dialog window for selecting a set of them.
   * NOTE: If necessary, add functionality to select only one.
   * @param category Either "paid": Read-only or "draft": Editable or "shared": Shared for approval.
   * @param title Title for the dialog.
   */
  public openSelectCalcs(category: "paid" | "draft" | "shared" = "draft", title: string = null): Promise<EditDialogResult<CalculationListItem[]>> {
    const calculations: CalculationListItem[] = [];
    title = title || (category === "paid" ? "Valitse maksetut palkat" : category == "shared" ? "Valitse lähetetyt":  "Valitse luonnokset" );
    return this.openEditDialog("salaxy-components/modals/calc/CalcList.html", calculations, { title, category });
  }

  /**
   * Opens the list of employment relations into a dialog window for selecting a set of them.
   * @param title Title for the dialog
   * @returns A set of selected item in dialog result.
   */
  public openSelectEmployments(title: string = null): Promise<EditDialogResult<EmploymentListItem[]>> {
    // HACK: The item now binds to old Worker API (that is really Employment based). Change to the latest Employment API.
    const workerList: EmploymentListItem[] = [];
    title = title || "Valitse työntekijät";
    return this.openEditDialog("salaxy-components/modals/calc/WorkerList.html", workerList, { title });
  }

  /**
   * Opens a search list of specified payload and returns one item.
   * @param title Title for the dialog
   * @returns Selected item or null if none was selected
   */
  public openSelect(title: string = null): Promise<DatasetListItem> {
    const workerList: DatasetListItem[] = [];
    title = title || "Valitse datasetti";
    return this.openEditDialog("salaxy-components/modals/ui/SelectListItem.html", workerList, { title }).then((result) => {
      if (result.action === EditDialogKnownActions.Ok && result.item.length) {
        return result.item[0];
      } else {
        return null;
      }
    });
  }

  /**
   * Caches data to controller: This is needed to avoid $digest loops in AngularJS controllers.
   * NOTE: Default data diff method uses angular.equals(), so it will not recognize difference in properties starting with "$" - most notably OData options.
   * @param controller Controller that is used for caching. A property $salaxyCache is added for caching if not there already.
   * @param cacheKey Name for the object that is being cached. Must be unique within the controller scope.
   * @param dataMethod Method that gets the data that is being cached.
   * @param dataDiffMethod Optional method that is used for determining whether the data has changed.
   * If not specified, the dataMethod is used for diff. Should be used if dataMethod returns a lot of data or if it is resource intensive.
   */
  public cache<T>(controller: angular.IController, cacheKey: string, dataMethod: () => T, dataDiffMethod?: () => any): T {
    if (!controller.$salaxyCache) {
      controller.$salaxyCache = {} as any;
    }
    const cache = controller.$salaxyCache;
    if (dataDiffMethod) {
      const keyValue = dataDiffMethod();
      if (!cache[cacheKey] || !angular.equals(cache[cacheKey].key, keyValue)) {
        cache[cacheKey] = {
          key: keyValue,
          data: dataMethod(),
        };
      }
      return cache[cacheKey].data;
    } else {
      const data = dataMethod();
      if (!angular.equals(cache[cacheKey], data)) {
        cache[cacheKey] = data;
      }
      return cache[cacheKey];
    }
  }
  /**
   * Download provide file
   * @param file File blob
   * @param fileName downloaded file name
   */
  public downloadFile(file: Blob, fileName: string) {
    saveAs(file, fileName);
  }

  /**
   * Opens browser internally generated Blob url
   * @param file File blob
   */
  public openFile(file: Blob) {
    this.$window.open(URL.createObjectURL(file), "_blank", "fullscreen=yes");
  }

  /**
   * Shows a dialog for running a batch process.
   * @param header Header text for the dialog
   * @param list List of items for the batch process: The method is run to each of these items.
   * @param method Method that is run for each of the items.
   * @param batchParamsTemplateUrl Template for user input of data about the action: The parameters for the batch process.
   * @param data Usecase specific data that is bound in the controller and can be used in the template and method.
   * @param columns Columns shown from the list may be specified here. Leave null for default columns.
   * @param chunkSize Optional chunk size for the batch process. This is used if the method supports arrays.
   * @param autoStart Optional. If true, the batch process starts immediately after the dialog is opened.
   * @param autoClose Optional. If true, the batch process dialog closes automatically after the process is done.
   * @param silent Optional. If autoStart and autoClose are true, the dialog is not shown if the list size is smaller than the chunk size.
   * This Tabulator configuration.
   */
  public showBatchProcessDialog<T>(
    header: string,
    list: T[],
    method: (
      /** Single item or array (for supporting chunks) in the list that is being processed. */
      itemOrArray: T | T[],
      /** Usecase specific data that is bound in the controller and can be used in the template and method.  */
      data: any
    ) => Promise<{ message: string, result: any}[]>,
    batchParamsTemplateUrl = "salaxy-components/modals/batch/default.html",
    data?: any,
    columns?: Array<any>,
    chunkSize?: number,
    autoStart?: boolean,
    autoClose?: boolean,
    silent?: boolean
  ) : Promise<({ done: boolean, results: { message: string, result: any}[] })> {
    data = data || {};
    data.dialogHeader = header;
    data.dialogList = list;
    data.gridColumns = columns;
    data.batchMethod = method;
    data.chunkSize = chunkSize;
    data.batchTemplateUrl = batchParamsTemplateUrl;
    data.autoStart = autoStart ?? false;
    data.autoClose = autoClose ?? false;
    data.silent = silent ?? false;
    data.done = false;

    if (data.autoStart && data.autoClose && data.silent && data.chunkSize && data.dialogList.length <= data.chunkSize) {
      const loader = this.showLoading(header ?? "SALAXY.UI_Terms.wait");
      data.dialogList.forEach((itemToProcess) => {
        itemToProcess.processed = "Processing...";
      });
      return method(data.dialogList, data).then((results) => {
        let i = 0;
        for (const itemToProcess of data.dialogList) {
          itemToProcess.processed = "OK " + (results[i].message);
          itemToProcess.processedResult = { message: results[i].message, result: results[i].result };
          i++;
        }
        loader.dismiss();
        return { done: true, results: data.dialogList.map((item) => item.processedResult ) };
      });
    }
    else
    {
      return this.showDialog("salaxy-components/modals/batch/dialog.html", "BatchProcessController", data, null, "xl").then(() => {
        if (data.done) {
          return { done: true, results: data.dialogList.map((item) => item.processedResult ) };
        }
        else
        {
          return { done: false, results: [] };
        }
      });
    }
  }

  /**
   * Converts bytes to blob.
   * @param bytes Bytes encoded in base64.
   * @param mimeType Mime type for the blob.
   */
  public bytes2Blob(bytes: string, mimeType: string): Blob {
    const byteArray = Uint8Array.from(
      atob(bytes)
        .split("") // to char array
        .map((char) => char.charCodeAt(0))
    );
    return new Blob([byteArray], { type: mimeType });
  }

  /**
   * Waits for a specified delay before resolving the promise.
   * Can be used with index related issues.
   */
  public wait(delay = 3000): Promise<void> {
    return new Promise(resolve => this.$timeout(() => resolve(), delay));
  }

  /**
   * Shows a dialog based on returnUrl parameter from the current url.
   */
  private showReturnUrlDialog() {
    const returnUrlParameters = this.getReturnUrlParameters();
    if (returnUrlParameters) {
      if (returnUrlParameters.returnUrl &&
        (returnUrlParameters.returnUrl.toLowerCase().startsWith("http:") ||
          returnUrlParameters.returnUrl.toLowerCase().startsWith("https:")
        )) {
        this.showDialog(
          "salaxy-components/modals/" + returnUrlParameters.componentTemplate + ".html",
          null).then(() => {
            this.$window.location.assign(returnUrlParameters.returnUrl);
          });
        this.$rootScope.$evalAsync(() => {
          this.$location.url("/");
        });
      } else {
        this.showDialog(
          "salaxy-components/modals/" + returnUrlParameters.componentTemplate + ".html",
          null);
        // async redirect immediately with dialog
        this.$rootScope.$evalAsync(() => {
          this.$location.url(returnUrlParameters.returnUrl);
        });
      }
    }
  }

  private getReturnUrlParameters() {
    const url = this.$location.url();
    if (url.indexOf("/dialog/") !== 0) {
      return null;
    }
    const dialog = url.substring("/dialog/".length, url.indexOf("?"));
    const returnUrl = this.$location.search().returnUrl;
    return {
      componentTemplate: dialog,
      returnUrl,
    };
  }

  private init() {
    this.$rootScope.$on("$locationChangeStart", () => {
      this.showReturnUrlDialog();
    });
  }
}